import { useLazyQuery, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Icon, IconAttention } from '@flixbus/honeycomb-icons-react';
import {
	Button,
	DataTable,
	DataTableHead,
	DataTableRow,
	LegacyGrid as Grid,
	LegacyGridCol as GridCol,
	Heading,
	List,
	Tooltip,
} from '@flixbus/honeycomb-react';

import Loader from 'common/Loader';
import NoData from 'common/NoData';
import PageSizer from 'common/PageSizer';
import Pagination from 'common/Pagination';
import WarningBanner from 'common/WarningBanner';
import { getTimezone } from 'utils/datetime';

import ManagementFilter from './components/ManagementFilter/ManagementFilter';
import { crewManagementTableHeaders, ERRORS } from './const';
import { FETCH_CREW_MEMBERS, FETCH_MEMBERS_COUNT } from './qql/crewManagement';
import { crewManagementText } from './text';

import styles from './styles.module.css';

const CrewManagement = () => {
	const [fetchMembers, { loading, error }] = useLazyQuery(FETCH_CREW_MEMBERS, {
		onCompleted: (data) => {
			setCrewMembers(data.crewMemberSearch.results);
			setPageCount(data.crewMemberSearch.page);
		},
		onError: () => setCrewMembers([]),
	});

	const { refetch: refetchProblematicCount } = useQuery(FETCH_MEMBERS_COUNT, {
		variables: {
			filter: {
				mode: 'problematic',
			},
		},
		onCompleted: (data) => {
			setProblematicMembersCount(data.crewMembersCount);
		},
		onError: () => {
			setProblematicMembersCount(-1);
		},
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	});

	const [problematicMembersCount, setProblematicMembersCount] = useState(0);
	const [pageCount, setPageCount] = useState(0);
	const [activePage, setActivePage] = useState(0);
	const [isIncompleteProfilesMode, setIsIncompleteProfilesMode] = useState(false);
	const [filter, setFilters] = useState({});
	const [crewMembers, setCrewMembers] = useState([]);

	const handleFilters = (dataFromFilters) => {
		const filterCombined = {
			...filter,
			...(dataFromFilters.search !== '' ? { text: dataFromFilters.search } : { text: null }),
			...(dataFromFilters.mobilePartner !== ''
				? { mobilityPartnerId: dataFromFilters.mobilePartner.key }
				: { mobilityPartnerId: null }),
			...(dataFromFilters.group !== '' && dataFromFilters.group !== '0000000000000000'
				? { groupsIds: [dataFromFilters.group] }
				: { groupsIds: null }),
			...(dataFromFilters.homeLocation !== ''
				? { currentHomeBaseCityId: dataFromFilters.homeLocation.key }
				: { currentHomeBaseCityId: null }),
			...(dataFromFilters.locationAvailabilityCity &&
			Array.isArray(dataFromFilters.locationAvailabilityCity) &&
			dataFromFilters.locationAvailabilityCity.length > 0
				? {
						locationAvailabilityCityIds: dataFromFilters.locationAvailabilityCity
							.map((city) => city.value || city.key || city.id)
							.filter(Boolean),
					}
				: { locationAvailabilityCityIds: null }),
		};
		const filterEvaluated = Object.fromEntries(
			Object.entries(filterCombined).filter(([_key, value]) => value !== null)
		);
		setFilters({ ...filterEvaluated });
	};
	const triggerCall = (filter, page = 0) => {
		const pagination = {
			page: page,
			size: 10,
		};

		const filterCombined = {
			...filter,
			...(isIncompleteProfilesMode ? { mode: 'problematic' } : {}),
		};

		const requestParams = {
			variables: {
				pagination,
				filter: filterCombined,
			},
		};
		fetchMembers(requestParams);
		refetchProblematicCount({ filter: { ...requestParams.variables.filter, mode: 'problematic' } });
		return;
	};

	useEffect(() => {
		setActivePage(0);
		triggerCall(filter);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter, isIncompleteProfilesMode]);

	useEffect(() => {
		triggerCall(filter, activePage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activePage]);

	const errorMap = (errors) => {
		return errors.map((error) => <li key={error}>{ERRORS[error]}</li>);
	};

	const renderLoadingContent = () => (
		<DataTableRow>
			<td colSpan={crewManagementTableHeaders.length}>
				<Loader />
			</td>
		</DataTableRow>
	);

	const renderData = () => {
		if (crewMembers.length === 0) {
			return (
				<DataTableRow>
					<td>
						<NoData mode="noresult" />
					</td>
				</DataTableRow>
			);
		}
		return crewMembers.map(
			({
				id,
				fullName,
				currentHomeBase,
				phone,
				mobilityPartnerShortName,
				driverCard,
				validationErrors,
			}) => (
				<DataTableRow
					key={id}
					extraClasses={`${styles.link} ${validationErrors.length > 0 ? styles.errorRow : ''}`}
				>
					<td>
						<Link
							to={`/crew-management/${id}`}
							target="_blank"
							className={`${styles.link} ${validationErrors.length > 0 ? styles.errorRow : ''}`}
						>
							<div className={styles.name}>
								{fullName}
								{validationErrors.length > 0 && (
									<Tooltip
										id={id}
										content={
											<>
												<Heading size={4}>Data missing</Heading>
												<List>{errorMap(validationErrors)}</List>
											</>
										}
										position="top"
										openOnHover
									>
										<Icon extraClasses={styles.danger} InlineIcon={IconAttention} size={4} />
									</Tooltip>
								)}
							</div>
						</Link>
					</td>

					<td>{`${driverCard === null ? 'No data' : driverCard}`}</td>
					<td>{currentHomeBase === null ? 'No data' : currentHomeBase.city.name}</td>
					<td>{phone === null ? 'No data' : phone}</td>
					<td>{mobilityPartnerShortName === null ? 'No data' : mobilityPartnerShortName}</td>
					<td>{crewManagementText.noDataLabel}</td>
				</DataTableRow>
			)
		);
	};

	return (
		<PageSizer>
			<div className={styles.headerWrapper}>
				<h1>{crewManagementText.crewManagementHeader}</h1>
				<div className={styles.header}>
					<span>{crewManagementText.currentTimezone}</span>
					<span className={styles.bold}>{` ${getTimezone()}`}</span>
					<p>{crewManagementText.notYourTimezone}</p>
				</div>
				<ManagementFilter
					isIncompleteProfilesSet={isIncompleteProfilesMode}
					cancelIncompleteProfilesMode={() => setIsIncompleteProfilesMode(false)}
					onFilterSubmitted={(e) => handleFilters(e)}
					problematicCount={problematicMembersCount}
				/>
				{!isIncompleteProfilesMode && problematicMembersCount > 0 && (
					<WarningBanner
						action={
							<Button size="sm" onClick={() => setIsIncompleteProfilesMode(true)}>
								{`${crewManagementText.warningBanner.showAll} (${problematicMembersCount})`}
							</Button>
						}
					>
						<strong>{crewManagementText.warningBanner.crewMemberMissingTitle}</strong>{' '}
						{crewManagementText.warningBanner.crewMemberMissingInfo}
					</WarningBanner>
				)}
				{(loading || crewMembers.length > 0) && (
					<DataTable
						extraClasses={styles.margin}
						headers={
							<DataTableHead>
								{crewManagementTableHeaders.map((header, index) => (
									<th key={index}>{header}</th>
								))}
							</DataTableHead>
						}
					>
						{loading ? renderLoadingContent() : renderData()}
					</DataTable>
				)}
				{!loading && !error && crewMembers.length === 0 && <NoData mode="noresult" />}
				{error && <NoData mode="error" />}
				<div className={styles.pagination}>
					<Grid align="center" justify="center">
						<GridCol inline>
							<Pagination
								activePage={activePage}
								setActivePage={setActivePage}
								pageCount={pageCount}
							/>
						</GridCol>
					</Grid>
				</div>
			</div>
		</PageSizer>
	);
};

export default CrewManagement;
